import React from "react";
import {
  IconWorld,
  IconFileText,
  IconFiles,
  IconBolt,
  IconPlugConnected,
  IconMessage2,
  IconBrandWhatsapp,
  IconBrandInstagram,
  IconCode,
  IconAdjustmentsHorizontal,
  IconSparkles,
  IconFilter,
  IconScript,
  IconDatabase,
  IconCircleCheckFilled,
  IconCircleFilled,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Link, useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";

import styles from "./styles.module.css";
import AiApi from "../../../api/ai";
import AgentInfoModal from "../agent-info";
import { getAiSetupStatus } from "../../../utils/ai-setup-status";

const tooltipProps = { show: 250, hide: 400 };

const AiFlowDiagram: React.FC = () => {
  const [showAgentModal, setShowAgentModal] = React.useState(false);
  const location = useLocation();
  const history = useHistory();
  const hasTrackedReturnRef = React.useRef(false);

  const { data } = useQuery({
    queryFn: () => AiApi.getAiStatus(),
    queryKey: ["getAiStatus"],
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const setupStatus = React.useMemo(
    () => getAiSetupStatus(data?.status),
    [data?.status]
  );

  const showImproveResponses =
    setupStatus.channelsDone &&
    (!setupStatus.knowledgeDone || !setupStatus.integrationsDone);

  React.useEffect(() => {
    if (hasTrackedReturnRef.current) return;
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get("welcomeCheckout");
    if (checkoutStatus !== "success" && checkoutStatus !== "cancel") return;

    hasTrackedReturnRef.current = true;
    if ((window as any).GA_INITIALIZED) {
      ReactGA.event("welcome_paywall_checkout_returned", {
        result: checkoutStatus,
      });
    }
    history.replace(location.pathname);
  }, [location.pathname, location.search, history]);

  return (
    <section className={styles.flowCard}>
      {showImproveResponses ? (
        <div className={styles.improveCard}>
          <div className={styles.improveHeader}>
            <strong>
              <FormattedMessage id="ai.home.improve.title" />
            </strong>
            <span>
              <FormattedMessage id="ai.home.improve.subtitle" />
            </span>
          </div>

          <div className={styles.improveList}>
            <Link to="/ai/website" className={styles.improveItem}>
              {setupStatus.knowledgeDone ? (
                <IconCircleCheckFilled
                  size={16}
                  className={styles.improveDoneIcon}
                />
              ) : (
                <IconCircleFilled size={16} className={styles.improvePendingIcon} />
              )}
              <div className={styles.improveText}>
                <span className={styles.improveLabel}>
                  <FormattedMessage id="nav.aiSetup.item.knowledge" />
                </span>
                <span className={styles.improveDescription}>
                  <FormattedMessage id="ai.home.improve.knowledge.desc" />
                </span>
              </div>
              <span className={styles.improveAction}>
                <FormattedMessage
                  id={setupStatus.knowledgeDone ? "common.done" : "nav.aiSetup.action.go"}
                />
              </span>
            </Link>

            <Link to="/ai/integrations" className={styles.improveItem}>
              {setupStatus.integrationsDone ? (
                <IconCircleCheckFilled
                  size={16}
                  className={styles.improveDoneIcon}
                />
              ) : (
                <IconCircleFilled size={16} className={styles.improvePendingIcon} />
              )}
              <div className={styles.improveText}>
                <span className={styles.improveLabel}>
                  <FormattedMessage id="nav.aiSetup.item.integrations" />
                </span>
                <span className={styles.improveDescription}>
                  <FormattedMessage id="ai.home.improve.integrations.desc" />
                </span>
              </div>
              <span className={styles.improveAction}>
                <FormattedMessage
                  id={setupStatus.integrationsDone ? "common.done" : "nav.aiSetup.action.go"}
                />
              </span>
            </Link>
          </div>
        </div>
      ) : null}

      <div className={styles.flowBoardWrapper}>
        <div className={styles.flowStage}>
          
          <svg className={styles.connectionsLayer} viewBox="0 0 900 450" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
                <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.2" />
              </linearGradient>
            </defs>

            <path className={styles.connectionPath} d="M450,115 L450,165" />
            <circle r="3" className={styles.particle}>
              <animateMotion dur="1.5s" repeatCount="indefinite" path="M450,115 L450,165" />
            </circle>

            <path className={styles.connectionPath} d="M210,225 L305,225" />
            <circle r="3" className={styles.particle} style={{ animationDelay: '0.5s' }}>
              <animateMotion dur="2s" repeatCount="indefinite" keyPoints="0;1;0" keyTimes="0;0.5;1" path="M210,225 L305,225" />
            </circle>

            <path className={styles.connectionPath} d="M595,225 L690,225" />
            <circle r="3" className={styles.particle}>
              <animateMotion dur="1.5s" repeatCount="indefinite" path="M595,225 L690,225" />
            </circle>

            <path className={styles.connectionPath} d="M450,335 L450,285" />
            <circle r="3" className={styles.particle} style={{ animationDelay: '1s' }}>
              <animateMotion dur="1.5s" repeatCount="indefinite" path="M450,335 L450,285" />
            </circle>
          </svg>

          <div className={`${styles.node} ${styles.nodeSources}`}>
            <div className={styles.nodeHeader}>
              <div className={styles.iconBox}><IconDatabase size={18} /></div>
              <span className={styles.nodeTitle}>
                <FormattedMessage id="ai.sidebar.sources" />
              </span>
            </div>
            <div className={styles.chipsGrid}>
              <OverlayTrigger
                placement="top"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id={data?.status?.website ? "ai.source.active" : "ai.source.inactive"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/website" className="no-underline">
                  <div className={`${styles.miniChip} ${data?.status?.website ? styles.miniChipActive : ""}`}>
                    <IconWorld size={12} />
                    <FormattedMessage id="ai.sources.website" />
                  </div>
                </Link>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id={data?.status?.text ? "ai.source.active" : "ai.source.inactive"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/text" className="no-underline">
                  <div className={`${styles.miniChip} ${data?.status?.text ? styles.miniChipActive : ""}`}>
                    <IconFileText size={12} />
                    <FormattedMessage id="ai.sources.text" />
                  </div>
                </Link>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id={data?.status?.files ? "ai.source.active" : "ai.source.inactive"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/files" className="no-underline">
                  <div className={`${styles.miniChip} ${data?.status?.files ? styles.miniChipActive : ""}`}>
                    <IconFiles size={12} />
                    <FormattedMessage id="ai.sources.files" />
                  </div>
                </Link>
              </OverlayTrigger>
            </div>
          </div>

          <Link to="/ai/integrations" className={`${styles.node} ${styles.nodeIntegrations}`}>
            <div className={styles.nodeHeader}>
              <div className={styles.iconBox}><IconPlugConnected size={18} /></div>
              <span className={styles.nodeTitle}>
                <FormattedMessage id="nav.integrations" />
              </span>
            </div>
            <p className={styles.nodeDescription}>CRM, Booking, APIs</p>
          </Link>

          <div
            className={`${styles.node} ${styles.nodeBot}`}
            role="dialog"
            onClick={() => setShowAgentModal(true)}
          >
            <div className={styles.botGlow} />
            <div className={styles.botIconWrapper}>
              <IconBolt size={32} stroke={1.5} />
            </div>
            <div className={styles.botContent}>
              <strong>
                <FormattedMessage id="ai.sources.agent" />
              </strong>
              <p>
                <FormattedMessage id="ai.sources.agent.desc" />
              </p>
            </div>
          </div>

          <div className={`${styles.node} ${styles.nodeChannels}`}>
            <div className={styles.nodeHeader}>
              <div className={styles.iconBox}><IconMessage2 size={18} /></div>
              <span className={styles.nodeTitle}>
                <FormattedMessage id="nav.channels" />
              </span>
            </div>
            <div className={styles.socialRow}>
              <OverlayTrigger
                placement="bottom"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    WhatsApp {" "}
                    <FormattedMessage id={data?.status?.wa ? "common.connected" : "common.disconnected"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/channels" className="lh-normal">
                  <IconBrandWhatsapp
                    size={20}
                    className={data?.status?.wa ? styles.activeChannel : styles.inactiveChannel}
                    role="tooltip"
                  />
                </Link>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    Instagram {" "}
                    <FormattedMessage id={data?.status?.ig ? "common.connected" : "common.disconnected"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/channels" className="lh-normal">
                  <IconBrandInstagram
                    size={20}
                    className={data?.status?.ig ? styles.activeChannel : styles.inactiveChannel}
                  />
                </Link>
              </OverlayTrigger>
              <OverlayTrigger
                placement="bottom"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    Web Chat {" "}
                    <FormattedMessage id="common.available" />
                  </Tooltip>
                }
              >
                <Link to="/ai/channels" className="lh-normal">
                  <IconCode size={20} className={styles.activeChannel} />
                </Link>
              </OverlayTrigger>
            </div>
          </div>

          <div className={`${styles.node} ${styles.nodeBehavior}`}>
            <div className={styles.nodeHeader}>
              <div className={styles.iconBox}><IconAdjustmentsHorizontal size={18} /></div>
              <span className={styles.nodeTitle}>
                <FormattedMessage id="ai.sidebar.behavior" />
              </span>
            </div>
            <div className={styles.chipsGrid}>
              <OverlayTrigger
                placement="top"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id="common.activeConfig" />
                  </Tooltip>
                }
              >
                <Link to="/ai/system-prompt" className="no-underline">
                  <div className={`${styles.miniChip} ${styles.miniChipActive}`}>
                    <IconScript size={12} />
                    <FormattedMessage id="ai.settings.instructions" />
                  </div>
                </Link>
              </OverlayTrigger>

              <OverlayTrigger
                placement="top"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id={data?.status?.escalations ? "ai.settings.escalations.enable" : "ai.settings.escalations.disabled"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/escalations" className="no-underline">
                  <div className={`${styles.miniChip} ${data?.status?.escalations ? styles.miniChipActive : ""}`}>
                    <IconSparkles size={12} />
                    <FormattedMessage id="ai.settings.escalations" />
                  </div>
                </Link>
              </OverlayTrigger>

              <OverlayTrigger
                placement="bottom"
                delay={tooltipProps}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id={data?.status?.followUps ? "ai.settings.followUps.enable" : "ai.settings.followUps.disabled"} />
                  </Tooltip>
                }
              >
                <Link to="/ai/follow-ups" className="no-underline">
                  <div className={`${styles.miniChip} ${data?.status?.followUps ? styles.miniChipActive : ""}`}>
                    <IconFilter size={12} />
                    <FormattedMessage id="ai.settings.followUps" />
                  </div>
                </Link>
              </OverlayTrigger>
            </div>
          </div>

        </div>
      </div>

      {showAgentModal && (
        <React.Suspense fallback="Loading...">
          <AgentInfoModal onClose={() => setShowAgentModal(false)} />
        </React.Suspense>
      )}
    </section>
  );
};

export default AiFlowDiagram;
